import { ScheduleFreshnessBadge } from '../../../entities/schedule';
import { DispositionRangeNav } from './DispositionRangeNav';
import { DispositionViewSwitch } from './DispositionViewSwitch';
import type { DispositionDay } from '../model/useDispositionDay';
import type { DispositionWeek } from '../model/useDispositionWeek';
import type { DispositionViewState } from '../model/useDispositionView';

const DAY_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

interface Props {
  view: DispositionViewState;
  day: DispositionDay;
  week: DispositionWeek;
  /** Freshness of the board below. See `useScheduleFreshness`. */
  lastUpdatedAt: number | null;
  isFetching: boolean;
  isDisconnected: boolean;
  onRefresh: () => void;
}

/**
 * The board's header row: navigate, where am I, which view, is this current.
 *
 * Those are four questions in three groups, and the row is built to say so. It used to be one flat
 * `gap-4` between every element, which gave a circular arrow, a pill, an underlined tab set and a
 * status dot equal standing and no relationship — four visual languages spaced identically. Here
 * the gaps carry the grouping: tight inside the nav cluster, wider before the view switch, and a
 * hairline rule before the status, which is the one thing on the row that is not a control.
 *
 * The divider matches `PageLayout.module.css`'s `.actionDivider` (1px × 24px, `#d1d5db`) rather
 * than inventing a second rule weight for the same job one layer up.
 *
 * Note this row does **not** push the status group to the right edge: `PageLayout`'s `.buttonRow`
 * is `flex-shrink: 0` and shrink-wraps its content, so there is no free width to push into. Making
 * it grow would mean editing a layout every page in the app shares, for one page's benefit.
 */
export const DispositionToolbar = ({
  view,
  day,
  week,
  lastUpdatedAt,
  isFetching,
  isDisconnected,
  onRefresh,
}: Props) => {
  const isWeek = view.value === 'woche';

  // The only real difference between a day header and a week header, in one place.
  const nav = isWeek
    ? {
        label: week.label,
        canGoToToday: !week.isCurrentWeek,
        onPrev: week.prev,
        onNext: week.next,
        onToday: week.goToToday,
        prevLabel: 'Vorherige Woche',
        nextLabel: 'Nächste Woche',
      }
    : {
        label: DAY_FORMATTER.format(new Date(`${day.dayIso}T00:00:00`)),
        canGoToToday: day.canGoToToday,
        onPrev: day.prev,
        onNext: day.next,
        onToday: day.goToToday,
        prevLabel: 'Vorheriger Tag',
        nextLabel: 'Nächster Tag',
      };

  return (
    <div className="flex items-center gap-4">
      <DispositionRangeNav
        onPrev={nav.onPrev}
        onNext={nav.onNext}
        onToday={nav.onToday}
        canGoToToday={nav.canGoToToday}
        prevLabel={nav.prevLabel}
        nextLabel={nav.nextLabel}
      />

      {/*
        The primary fact on the row, and the reason the min-width survives the move: German long
        dates swing about 30px between "Mittwoch, 3. September 2026" and "Dienstag, 25. August
        2026", and everything to the right of this would step sideways on every arrow tap without
        a floor. Left-aligned now rather than right, so the label itself no longer slides either.
      */}
      <span className="min-w-[260px] whitespace-nowrap font-['PT_Sans'] text-[20px] font-bold leading-none text-[#004494] max-[1080px]:min-w-[210px] max-[1080px]:text-[16px]">
        {nav.label}
      </span>

      <div className="ml-2 flex items-center gap-4">
        {view.canSwitch && (
          <>
            <DispositionViewSwitch view={view} />
            <span className="h-6 w-px shrink-0 bg-[#d1d5db]" aria-hidden />
          </>
        )}

        <ScheduleFreshnessBadge
          lastUpdatedAt={lastUpdatedAt}
          isFetching={isFetching}
          isDisconnected={isDisconnected}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
};
